/**
 * Leader Line Utility
 * Creates SVG paths connecting labels to visual elements
 * Based on scroll.md Pattern 4: "Technical Leader Lines"
 */

export interface LeaderLineConfig {
    startEl: HTMLElement;
    endEl: HTMLElement;
    startAnchor?: 'left' | 'right' | 'top' | 'bottom' | 'center';
    endAnchor?: 'left' | 'right' | 'top' | 'bottom' | 'center';
    color?: string;
    strokeWidth?: number;
    elbow?: boolean; // Use elbow (L-shaped) vs direct line
}

interface Point {
    x: number;
    y: number;
}

/**
 * Get anchor point on an element
 */
function getAnchorPoint(
    el: HTMLElement,
    anchor: 'left' | 'right' | 'top' | 'bottom' | 'center',
    container: HTMLElement
): Point {
    const rect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const relativeX = rect.left - containerRect.left;
    const relativeY = rect.top - containerRect.top;

    switch (anchor) {
        case 'left':
            return { x: relativeX, y: relativeY + rect.height / 2 };
        case 'right':
            return { x: relativeX + rect.width, y: relativeY + rect.height / 2 };
        case 'top':
            return { x: relativeX + rect.width / 2, y: relativeY };
        case 'bottom':
            return { x: relativeX + rect.width / 2, y: relativeY + rect.height };
        case 'center':
        default:
            return { x: relativeX + rect.width / 2, y: relativeY + rect.height / 2 };
    }
}

/**
 * Create SVG path string for leader line
 */
function createLeaderPath(
    start: Point,
    end: Point,
    elbow: boolean
): string {
    if (!elbow) {
        // Direct line
        return `M ${start.x},${start.y} L ${end.x},${end.y}`;
    }

    // Elbow line (L-shaped)
    // First go horizontal, then vertical
    const midX = start.x + (end.x - start.x) * 0.7;
    return `M ${start.x},${start.y} L ${midX},${start.y} L ${midX},${end.y} L ${end.x},${end.y}`;
}

/**
 * Create a leader line SVG element
 */
export function createLeaderLine(
    config: LeaderLineConfig,
    container: HTMLElement
): SVGPathElement {
    const {
        startEl,
        endEl,
        startAnchor = 'right',
        endAnchor = 'left',
        color = 'var(--neon-cyan)',
        strokeWidth = 0.5,
        elbow = true
    } = config;

    const startPoint = getAnchorPoint(startEl, startAnchor, container);
    const endPoint = getAnchorPoint(endEl, endAnchor, container);

    const pathData = createLeaderPath(startPoint, endPoint, elbow);

    // Create SVG path element
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('stroke', color);
    path.setAttribute('stroke-width', strokeWidth.toString());
    path.setAttribute('fill', 'none');
    path.setAttribute('opacity', '0.6');
    path.classList.add('leader-line');

    // Calculate path length for animation
    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length}`;
    path.style.strokeDashoffset = `${length}`;

    return path;
}

/**
 * Animate leader line drawing
 */
export function animateLeaderLine(
    path: SVGPathElement,
    duration: number = 800,
    delay: number = 0
): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(() => {
            path.style.transition = `stroke-dashoffset ${duration}ms linear`;
            path.style.strokeDashoffset = '0';

            setTimeout(resolve, duration);
        }, delay);
    });
}

/**
 * Recalculate leader line path (for resize)
 */
export function updateLeaderLine(
    path: SVGPathElement,
    config: LeaderLineConfig,
    container: HTMLElement
): void {
    const {
        startEl,
        endEl,
        startAnchor = 'right',
        endAnchor = 'left',
        elbow = true
    } = config;

    const startPoint = getAnchorPoint(startEl, startAnchor, container);
    const endPoint = getAnchorPoint(endEl, endAnchor, container);

    const pathData = createLeaderPath(startPoint, endPoint, elbow);
    path.setAttribute('d', pathData);

    // Recalculate length
    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length}`;
}

/**
 * Create multiple leader lines with staggered animation
 */
export async function createLeaderLineGroup(
    configs: LeaderLineConfig[],
    container: HTMLElement,
    svgContainer: SVGSVGElement,
    staggerDelay: number = 200
): Promise<SVGPathElement[]> {
    const paths: SVGPathElement[] = [];

    for (let i = 0; i < configs.length; i++) {
        const path = createLeaderLine(configs[i], container);
        svgContainer.appendChild(path);
        paths.push(path);

        // Animate with stagger
        animateLeaderLine(path, 800, i * staggerDelay);
    }

    return paths;
}
